import React from "react";

export function formatAddress(
  address: string,
  length = 12,
  onlyMobile = false,
  longerStart = false
): string {
  const isMobileView = window.innerWidth < 768;

  if ((!isMobileView && onlyMobile) || address.length <= length) {
    return address;
  }

  const halfLength = Math.floor(length / 2);
  const start = address.substring(0, halfLength + 4 * Number(longerStart));
  const end = address.substring(address.length - halfLength);

  return start + "..." + end;
}

/**
 * Remove _TX from transaction type values and replace _ with space " "
 * @param {string} type
 */
export function formatTransactionType(type: string): string {
  if (type.endsWith("_TX")) {
    return type.slice(0, -3).replace("_", " ").toLowerCase();
  }
  return type.toLowerCase();
}

export function transformToLowerExceptFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Rounds a number down to the specified number of decimal places.
 * @param number - The number to round down.
 * @param decimals - The number of decimal places to round down to.
 * @returns The rounded down number.
 */
function roundDown(number: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.floor(number * factor) / factor;
}

/**
 * Formats the amount with FLR
 * @param amount - The amount to format
 * @param convert - Whether to convert the amount from Gwei to FLR
 * @param digits - The number of decimal places to display
 * @param roundDownFlag - Whether to round down the amount
 * @returns {React.ReactNode}
 */
export const formatAmountWithLabel = (
  amount: number,
  convert = true,
  digits?: number,
  roundDownFlag = false
): React.ReactNode => {
  return (
    <>
      <span>{formatNumber(amount, convert, digits, roundDownFlag)}</span>
      &nbsp;
      <span style={{ fontWeight: 400 }}>FLR</span>
    </>
  );
};

/**
 * Safely parse a gwei amount (string from the API, or legacy number) to bigint.
 * Returns null for null/undefined, empty/whitespace strings, non-finite
 * numbers, and non-integer strings ("1.5", "1e19", garbage).
 */
export function toBigIntSafe(
  value: string | number | null | undefined
): bigint | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    return BigInt(Math.trunc(value));
  }
  const trimmed = value.trim();
  if (trimmed === "") return null; // BigInt("") is 0n — reject explicitly
  try {
    return BigInt(trimmed);
  } catch {
    return null;
  }
}

/**
 * Format a gwei amount as millions of FLR (gwei / 1e15), precision-correct
 * via BigInt division with half-up rounding to `digits` decimals.
 * Returns null when the value is missing, unparseable, or zero (callers
 * render "n/a", matching the previous falsy-number behavior).
 */
export function formatGweiMillions(
  value: string | number | null | undefined,
  digits = 1
): string | null {
  const big = toBigIntSafe(value);
  if (big === null || big === 0n) return null;
  const divisor = 10n ** BigInt(15 - digits);
  const rounded =
    big >= 0n ? (big + divisor / 2n) / divisor : (big - divisor / 2n) / divisor;
  return formatNumber(Number(rounded) / 10 ** digits, false, digits);
}

/**
 * 5% of a gwei total as a number (0.05 = 1/20 exactly, so BigInt(total)/20n).
 * The Number() conversion error is ≤ 0.5 ulp — negligible for the capLimit
 * UI threshold. Returns null when the value is unparseable.
 */
export function fivePercentOfGwei(
  value: string | number | null | undefined
): number | null {
  const big = toBigIntSafe(value);
  if (big === null) return null;
  return Number(big / 20n);
}

export function convertFromGwei(amount: number): number {
  return amount / 1e9;
}

export function convertFromWei(amount: number): number {
  return amount / 1e18;
}

export function formatNumber(
  amount: number,
  convert = true,
  digits?: number,
  roundDownFlag = false
): string {
  const converted = convert ? convertFromGwei(amount) : amount;
  const valueToFormat = roundDownFlag
    ? roundDown(converted, digits ?? 2)
    : converted;
  return new Intl.NumberFormat(Intl.NumberFormat().resolvedOptions().locale, {
    maximumFractionDigits: digits ?? 20,
    minimumFractionDigits: digits ?? 2,
  }).format(valueToFormat);
}

/**
 * Parse a localized number to a float.
 * @param {string} stringNumber - the localized number
 * @param {string} locale - [optional] the locale that the number is represented in. Omit this parameter to use the current locale.
 */
export function parseLocaleNumber(
  stringNumber: string,
  locale?: string
): number {
  const thousandSeparator: string = Intl.NumberFormat(locale)
    .format(11111)
    .replace(/\p{Number}/gu, "");
  const decimalSeparator: string = Intl.NumberFormat(locale)
    .format(1.1)
    .replace(/\p{Number}/gu, "");

  return parseFloat(
    stringNumber
      .replace(new RegExp("\\" + thousandSeparator, "g"), "")
      .replace(new RegExp("\\" + decimalSeparator), ".")
  );
}

export function getYearlyROI(epochPercent: number): number {
  return (epochPercent / 3.5) * 365;
}

export function shortenText(text: string, maxLength = 20): string {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength - 3) + "...";
}
